import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { login } from '../../store/actions/authThunks';
import LoginForm from './LoginForm';
import useAuthRedirect from '../../hooks/useAuthRedirect';
import ROUTES from '../../constants/routes';

/**
 * LoginContainer.jsx
 *
 * - Dispatches the login thunk when the user submits credentials.
 * - Uses useAuthRedirect() to get:
 *    • redirectTo         → route to navigate to (string) or null if not yet appropriate
 *    • isLoadingRedirect  → true if PRIMARY_ADMIN with orgId is still pending onboarding
 *    • redirectError      → onboarding fetch error, if any (optional here)
 * - If isLoadingRedirect === true → show null or a loading spinner.
 * - If redirectTo is a route (not null), perform navigate(redirectTo).
 * - In all other cases, render <LoginForm>.
 */
const LoginContainer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { isLoading, error, isAuthenticated } = useSelector(
    (state) => ({
      isLoading: state.loading.login,
      error: state.auth.error || '',
      isAuthenticated: state.auth.isAuthenticated,
    }),
    shallowEqual,
  );

  // Use the hook that centralizes all redirection logic
  const { redirectTo, isLoadingRedirect } = useAuthRedirect();

  // After login success we must re-render (isAuthenticated) and then redirect when redirectTo is set
  useEffect(() => {
    if (isAuthenticated && redirectTo) {
      navigate(redirectTo);
    }
  }, [isAuthenticated, redirectTo, navigate]);

  const handleLogin = useCallback(
    async ({ email, password, rememberMe }) => {
      try {
        const result = await dispatch(login({ username: email, password, rememberMe })).unwrap();
        const path = result?.redirectTo ?? ROUTES.FACILITY_SELECTION;
        navigate(path);
      } catch {
        // Error is already in state.auth.error (rejectWithValue); LoginForm shows serverError
      }
    },
    [dispatch, navigate],
  );

  // If PRIMARY_ADMIN with orgId is waiting for onboarding → show null (or a spinner)
  if (isLoadingRedirect) {
    return null; // O <Spinner text="Verificando estado de organización…" />;
  }

  return <LoginForm onLogin={handleLogin} isLoading={isLoading} serverError={error} />;
};

export default LoginContainer;
